import { ArrowRight, BookMarked } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PLACEMENT_IDS, PLACEMENTS, type Placement } from '@/core/project/placement';
import type { Preset } from '@/core/project/preset';
import { cn } from '@/lib/cn';
import { PlacementFacts } from '../PlacementFacts';
import { usePlacementText } from '../use-placement-text';
import { DropTarget } from './IntentBands';

export interface FitPanelProps {
  readonly placement: Placement;
  /** The built-in recipe the link also asked for (`?for=privacy&fit=youtube`), applied before the fit. */
  readonly recipe: Preset | null;
  readonly onPlacement: (placement: Placement) => void;
  readonly onFiles: (files: File[]) => void;
  /** Back to the recipes: the visitor wants to edit without fitting. */
  readonly onEdit: () => void;
  readonly dragging: boolean;
  readonly loading: string | false;
}

/**
 * Fitting for a place (`/?fit=instagram-reels`, the spec pages' button): what
 * the place asks for, the place still changeable, and where to drop the video.
 * The video opens in the editor, reframed, so the crop can be checked and the
 * length trimmed.
 */
export function FitPanel({ placement, recipe, onPlacement, onFiles, onEdit, dragging, loading }: FitPanelProps) {
  const { t } = useTranslation();
  const text = usePlacementText();
  const place = text.name(placement);
  return (
    <section aria-labelledby="fit-title" className="border-t border-line-strong pt-4">
      <div className="-mx-3 grid gap-x-8 gap-y-5 rounded-[22px] bg-panel p-4 shadow-float ring-1 ring-line sm:-mx-5 sm:p-5 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-center">
        <div className="min-w-0">
          <h2 id="fit-title" className="text-[21px] leading-tight font-semibold tracking-[-0.03em] text-balance text-fg sm:text-[24px]">
            {t('home.fit.title', { place })}
          </h2>
          <p className="mt-1.5 max-w-120 text-[15px] leading-snug text-pretty text-fg-muted">
            {placement.frame === 'original' ? t('home.fit.bodyOriginal', { place }) : t('home.fit.body', { place, shape: placement.frame })}
          </p>
          <PlacementFacts placement={placement} className="mt-4" />
          {recipe?.builtIn && (
            <p className="mt-3 flex max-w-120 items-start gap-2 text-[13px] leading-snug text-fg-muted">
              <BookMarked size={14} className="mt-0.5 shrink-0 text-accent-text" aria-hidden />
              {t('home.fit.withRecipe', { recipe: t(`home.intents.${recipe.builtIn}.title`), promise: t(`home.intents.${recipe.builtIn}.promise`) })}
            </p>
          )}
          <p className="mt-4 mb-2 text-[13px] font-medium text-fg-subtle">{t('home.fit.places')}</p>
          <div role="radiogroup" aria-label={t('home.fit.places')} className="flex flex-wrap gap-1.5">
            {PLACEMENT_IDS.map((id) => (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={id === placement.id}
                onClick={() => onPlacement(PLACEMENTS[id])}
                className={cn(
                  'h-8 rounded-lg px-2.5 text-[13px] font-medium ring-1 transition-colors duration-150 ring-inset outline-none focus-visible:ring-2 focus-visible:ring-accent',
                  id === placement.id ? 'bg-accent-soft text-fg ring-accent-strong/60' : 'bg-elevated text-fg-muted ring-line hover:text-fg',
                )}
              >
                {text.short(PLACEMENTS[id])}
              </button>
            ))}
          </div>
        </div>
        <DropTarget dragging={dragging} loading={loading} onFiles={onFiles} hint={t('home.fit.hint')} />
      </div>
      <button
        type="button"
        onClick={onEdit}
        className="group mt-4 inline-flex items-center gap-1.5 rounded-md px-2 text-[13px] font-medium text-fg-muted transition-colors duration-200 outline-none hover:text-fg focus-visible:ring-2 focus-visible:ring-accent"
      >
        {t('home.fit.edit')}
        <ArrowRight size={14} className="transition-transform duration-200 ease-out-soft group-hover:translate-x-0.5" />
      </button>
    </section>
  );
}
